import axios from "axios";
import { cacheGetResponse, flushOfflineQueue, loadCachedGetResponse, queueOfflineRequest } from "@/lib/offlineStore";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export const API_BASE = `${BACKEND_URL}/api`;
let csrfToken = "";

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  if (["post", "put", "patch", "delete"].includes(config.method?.toLowerCase()) && csrfToken) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = csrfToken;
  }
  if (config.offlineQueue && config.idempotencyKey) {
    config.headers = config.headers || {};
    config.headers["Idempotency-Key"] = config.idempotencyKey;
    if (config.data && typeof config.data === "object" && !(typeof FormData !== "undefined" && config.data instanceof FormData)) {
      config.data = { ...config.data, idempotency_key: config.data.idempotency_key || config.idempotencyKey };
    }
  }
  return config;
});

api.interceptors.response.use(
  async (response) => {
    const responseCsrfToken = response.headers?.["x-csrf-token"];
    if (responseCsrfToken) csrfToken = responseCsrfToken;
    if (typeof window !== "undefined") window.dispatchEvent(new Event("egs-network-online"));
    if (response.config?.method?.toLowerCase() === "get") {
      try { await cacheGetResponse(response.config, response.data); } catch { /* Cache failure must not fail a live request. */ }
    }
    return response;
  },
  async (error) => {
    const config = error?.config;
    if (!config) return Promise.reject(error);
    if (error.response?.status === 403 && String(error.response?.data?.detail || "").startsWith("CSRF token") && !config.csrfRetried) {
      config.csrfRetried = true;
      try { await api.get("/auth/csrf", { offlineCache: false }); } catch { /* fall through to the original error */ }
      if (csrfToken) {
        config.headers = config.headers || {};
        config.headers["X-CSRF-Token"] = csrfToken;
        return api(config);
      }
    }
    const transient = !error.response || [408, 429, 502, 503, 504].includes(error.response.status);
    if (transient && typeof window !== "undefined") window.dispatchEvent(new Event("egs-network-offline"));

    if (config.method?.toLowerCase() === "get" && transient && config.offlineCache !== false) {
      try {
        const cached = await loadCachedGetResponse(config);
        if (cached) return { data: cached.data, status: 200, statusText: "Offline cache", headers: {}, config, offline: true, cachedAt: cached.cached_at };
      } catch { /* Fall through to the original network error. */ }
    }

    if (config.offlineQueue && config.idempotencyKey && transient) {
      try {
        const queued = await queueOfflineRequest(config, config.idempotencyKey);
        return {
          data: { offline_queued: true, ...queued, message: "Saved on this device and will sync when the connection is restored." },
          status: 202,
          statusText: "Accepted offline",
          headers: {},
          config,
          offline: true,
        };
      } catch { /* Fall through; unsupported requests remain visible as failures. */ }
    }
    return Promise.reject(error);
  },
);

let outboxFlushPromise = null;
export function flushOfflineApiQueue() {
  if (outboxFlushPromise || !navigator.onLine) return outboxFlushPromise || Promise.resolve({ sent: 0, failed: 0 });
  outboxFlushPromise = flushOfflineQueue((request) => api.request({
    method: request.method,
    url: request.url,
    baseURL: request.baseURL,
    params: request.params,
    data: request.data,
    headers: { ...request.headers, "Idempotency-Key": request.idempotency_key },
    offlineQueue: false,
  })).finally(() => { outboxFlushPromise = null; });
  return outboxFlushPromise;
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flushOfflineApiQueue().catch(() => {}); });
}

// Helper to normalise FastAPI validation error arrays into strings
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatGHS(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency: "GHS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatNumber(value, digits = 2) {
  return new Intl.NumberFormat("en-GH", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));
}

export default api;
